import { ROUTES } from '@repo/shared-types/routeRegistry';
import { parseAppSearch, stringifyAppQuery } from './parseAppQuery';

export type ResolveCanonicalUser = {
  isSelfManagingOwner: boolean;
};

export type ResolveCanonicalResult = {
  pathname: string;
  search: string;
  action: 'redirect' | 'none';
};

type State = { pathname: string; search: string };

const OWNER_PORTAL_FIRST_SEGMENTS = new Set([
  'statements',
  'properties',
  'reporting',
  'dashboard',
  'communication',
  'documents',
  'settings',
]);

/** Phase 1: `/app/owners/<portal>/...` → `/app/owner/<portal>/...` (canonical owner workspace). */
function normalizeWorkspacePrefix(state: State): State {
  const p = state.pathname;
  if (!p.startsWith('/app/owners/')) return state;
  const rest = p.slice('/app/owners/'.length);
  if (!rest) return state;
  const first = rest.split('/')[0] ?? '';
  if (!OWNER_PORTAL_FIRST_SEGMENTS.has(first)) return state;
  return { pathname: `/app/owner/${rest}`, search: state.search };
}

/** Phase 2: SMO default home; all users: legacy bills → ops bills. */
function legacyPathRedirects(state: State, user: ResolveCanonicalUser | null): State {
  let s = state;
  if (user?.isSelfManagingOwner && s.pathname === ROUTES.APP && s.search === '') {
    s = { pathname: ROUTES.OWNER_STATEMENTS, search: '' };
  }
  if (s.pathname === ROUTES.BILLS) {
    s = { pathname: ROUTES.OPS_ACCOUNTING_BILLS, search: s.search };
  }
  if (s.pathname === '/app/maintenance/work-orders') {
    s = { pathname: ROUTES.MAINTENANCE, search: s.search };
  }
  return s;
}

/** PM-only paths SMO must not use (external owner directory / PM owner admin). */
const SMO_BLOCKED_EXACT_PATHS = new Set<string>([
  ROUTES.OWNERS_MANAGEMENT,
  ROUTES.PROPERTY_OWNERS,
]);

/**
 * Phase 3: SMO users use the same `/app/*` ops routes as {@link SMO_PM_NAV}; only block external PM owner admin paths.
 */
function isSmoBlockedPath(pathname: string): boolean {
  if (SMO_BLOCKED_EXACT_PATHS.has(pathname)) return true;
  const ownersPrefix = '/app/owners/';
  if (!pathname.startsWith(ownersPrefix)) return false;
  const rest = pathname.slice(ownersPrefix.length);
  const first = rest.split('/')[0] ?? '';
  if (!first) return true;
  return !OWNER_PORTAL_FIRST_SEGMENTS.has(first);
}

function enforceRouteAllowlist(state: State, user: ResolveCanonicalUser | null): State {
  if (!user?.isSelfManagingOwner) return state;
  const p = state.pathname;
  if (!p.startsWith('/app')) return state;
  if (isSmoBlockedPath(p)) {
    return { pathname: ROUTES.OWNER_STATEMENTS, search: state.search };
  }
  return state;
}

/** Phase 4: Drop sheet-only id params that do not belong on the current pathname (prevents stale overlays). */
function validateSheetParams(state: State): State {
  const q = parseAppSearch(state.search);
  let dirty = false;
  const p = state.pathname;
  const onBillsPath =
    p === ROUTES.BILLS || p === ROUTES.OPS_ACCOUNTING_BILLS || p.endsWith('/accounting/bills');
  if (!onBillsPath) {
    for (const k of ['billId', 'matchId', 'billIds'] as const) {
      if (q[k] !== undefined) {
        delete q[k];
        dirty = true;
      }
    }
  }
  const onLeasesPath = p.includes('/leasing/leases');
  if (!onLeasesPath && q.leaseId !== undefined) {
    delete q.leaseId;
    dirty = true;
  }
  const onGuestCards = p.includes('/guest-cards');
  if (!onGuestCards && q.leadId !== undefined) {
    delete q.leadId;
    dirty = true;
  }
  const onUnits = p.includes('/properties/units');
  if (!onUnits) {
    for (const k of ['unitId', 'bulkKind'] as const) {
      if (q[k] !== undefined) {
        delete q[k];
        dirty = true;
      }
    }
  }
  const onTurns = p.includes('/maintenance/turns');
  if (!onTurns && q.turnId !== undefined) {
    delete q.turnId;
    dirty = true;
  }
  const onInspections = p.includes('/maintenance/inspections') || p.includes('/compliance/inspections');
  if (!onInspections && q.inspectionId !== undefined) {
    delete q.inspectionId;
    dirty = true;
  }
  const onReceivablesOrPayments =
    p.includes('/accounting/receivables') ||
    p.includes('/accounting/payments') ||
    p.endsWith('/payments');
  if (!onReceivablesOrPayments && q.receivableId !== undefined) {
    delete q.receivableId;
    dirty = true;
  }
  const onGovernance = p.includes('/compliance/governance');
  if (!onGovernance && q.logId !== undefined) {
    delete q.logId;
    dirty = true;
  }
  return dirty ? { ...state, search: stringifyAppQuery(q) } : state;
}

function phaseLegacyAppDevWarn(state: State): State {
  if (!import.meta?.env?.DEV) return state;
  const p = state.pathname;
  if (
    p.startsWith('/app/') &&
    !p.startsWith('/app/ops') &&
    !p.startsWith('/app/owner/') &&
    !p.startsWith('/app/owners') &&
    p !== ROUTES.UNAUTHORIZED &&
    !p.startsWith('/login') &&
    !p.startsWith('/sign-up') &&
    !p.startsWith('/complete-onboarding') &&
    !p.startsWith('/public-site') &&
    !p.startsWith('/legal/')
  ) {
    // eslint-disable-next-line no-console
    console.warn('[navigation] Legacy /app path detected (consider /app/ops workspace URL)', p);
  }
  return state;
}

function finalize(state: State, initial: State): ResolveCanonicalResult {
  const changed = state.pathname !== initial.pathname || state.search !== initial.search;
  return {
    pathname: state.pathname,
    search: state.search,
    action: changed ? 'redirect' : 'none',
  };
}

/**
 * Pure redirect policy (no `history` access). Call from `AppRouter` then apply via `writeHistory` when `action === 'redirect'`.
 * Invariant: `resolve(resolve(u))` should be stable for fixed inputs (idempotent for common rows).
 *
 * Phases: normalizeWorkspacePrefix → legacyPathRedirects → enforceRouteAllowlist → validateSheetParams → dev warn.
 */
export function resolveCanonicalUrl(input: {
  pathname: string;
  search: string;
  user: ResolveCanonicalUser | null;
}): ResolveCanonicalResult {
  const initial: State = { pathname: input.pathname, search: input.search };
  let state: State = { ...initial };
  state = normalizeWorkspacePrefix(state);
  state = legacyPathRedirects(state, input.user);
  state = enforceRouteAllowlist(state, input.user);
  state = validateSheetParams(state);
  state = phaseLegacyAppDevWarn(state);
  return finalize(state, initial);
}
